<script setup lang="ts">
import { ArrowLeft, CalendarDays, CircleCheck, MapPin, ShieldCheck, UsersRound } from '../components/wx-icons'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import StatePanel from '../components/StatePanel.vue'
import { api, dataOf } from '../services/api'
import { authState } from '../services/auth'
import { dateLabel, ntrpRange, statusText } from '../services/format'

const route = useRoute()
const router = useRouter()
const activity = ref<any>(null)
const loading = ref(true)
const error = ref('')
const acting = ref(false)
const id = computed(() => String(route.params.id || ''))
const enrolled = computed(() => Boolean(activity.value?.hasEnrolled || activity.value?.myEnrollment || [0, 1].includes(Number(activity.value?.myEnrollmentStatus))))
const canJoin = computed(() => statusText(activity.value?.status, activity.value?.date, activity.value?.startTime, activity.value?.endTime) === '报名中')
const isCreator = computed(() => {
  const creatorId = activity.value?.creatorId || activity.value?.creator?.id
  return Boolean(authState.value?.user.id && String(creatorId) === authState.value.user.id)
})

async function load() {
  loading.value = true
  error.value = ''
  try { activity.value = dataOf(await api<any>(`/activity/${encodeURIComponent(id.value)}`)) }
  catch (reason: any) { error.value = reason?.message || '活动加载失败' }
  finally { loading.value = false }
}

async function toggleEnrollment() {
  if (!authState.value) return router.push({ name: 'login', query: { redirect: route.fullPath } })
  if (acting.value) return
  acting.value = true
  try {
    await api(`/activity/${encodeURIComponent(id.value)}/${enrolled.value ? 'unenroll' : 'enroll'}`, { method: 'POST', body: '{}' })
    await load()
  } catch (reason: any) {
    window.alert(reason?.message || '操作失败')
  } finally { acting.value = false }
}

async function cancelActivity() {
  if (!window.confirm('确定取消这场活动吗？已报名和候补球友将收到通知。')) return
  acting.value = true
  try {
    await api(`/activity/${encodeURIComponent(id.value)}/cancel`, { method: 'POST', body: JSON.stringify({ reason: '发起人通过 Website 取消活动' }) })
    await load()
  } catch (reason: any) { window.alert(reason?.message || '取消失败') }
  finally { acting.value = false }
}

onMounted(load)
</script>

<template>
  <div class="shell detail-page">
    <button class="back-link" type="button" @click="router.back()"><ArrowLeft :size="18" />返回</button>
    <StatePanel v-if="loading" mode="loading" />
    <StatePanel v-else-if="error" mode="error" :description="error" @retry="load" />
    <template v-else-if="activity">
      <section class="activity-detail-head">
        <div>
          <div class="tag-row"><span class="status-pill">{{ statusText(activity.status, activity.date, activity.startTime, activity.endTime) }}</span><span v-for="tag in activity.tags || []" :key="tag.code || tag" class="chip">{{ tag.displayName || tag.name || tag }}</span></div>
          <h1>{{ activity.title || '一起打网球' }}</h1>
          <p>{{ activity.description || '发起人暂未填写补充说明。' }}</p>
        </div>
        <div class="activity-big-date"><b>{{ String(activity.date || '').slice(5, 7) }}</b><span>{{ String(activity.date || '').slice(8, 10) }}</span></div>
      </section>
      <div class="detail-layout activity-detail-layout">
        <div class="detail-main">
          <section class="content-card fact-list">
            <div><CalendarDays :size="21" /><span><small>活动时间</small><b>{{ dateLabel(activity.date, activity.startTime, activity.endTime) }}</b></span></div>
            <div><MapPin :size="21" /><span><small>活动地点</small><b>{{ activity.venueName || activity.customVenueName || activity.address || '地点待定' }}</b></span></div>
            <div><UsersRound :size="21" /><span><small>人数</small><b>{{ activity.enrolledCount || 0 }} / {{ activity.capacity || activity.totalSlots || '—' }} 人</b></span></div>
            <div><ShieldCheck :size="21" /><span><small>建议水平</small><b>{{ ntrpRange(activity.levelMin, activity.levelMax) }}</b></span></div>
          </section>
          <section class="content-card"><h2>参与球友</h2><div v-if="activity.participants?.length" class="people-grid"><div v-for="person in activity.participants" :key="person.id" class="person"><span>{{ String(person.nickname || person.name || '球').slice(0, 1) }}</span><b>{{ person.nickname || person.name || '球友' }}</b></div></div><StatePanel v-else mode="empty" title="等待第一位球友加入" /></section>
        </div>
        <aside class="detail-aside sticky-aside">
          <div class="join-card">
            <span>报名费用</span><strong>{{ Number(activity.price || 0) > 0 ? `¥${activity.price}` : '免费' }}</strong><small>{{ activity.feeMode === 'aa' ? '实际费用按参与人数均摊' : activity.feeMode === 'fixed' ? '每人固定费用' : '无需报名费' }}</small>
            <button class="button button-primary button-block" type="button" :disabled="acting || (!enrolled && !canJoin)" @click="toggleEnrollment"><CircleCheck :size="19" />{{ enrolled ? '取消报名' : canJoin ? '报名参加' : '当前不可报名' }}</button>
            <template v-if="isCreator"><RouterLink class="button button-secondary button-block" :to="`/activities/${id}/edit`">编辑活动</RouterLink><button class="danger-link" type="button" :disabled="acting" @click="cancelActivity">取消活动</button></template>
            <p v-if="activity.requiresEnrollmentApproval">报名后将进入候补，需等待发起人确认。</p>
          </div>
        </aside>
      </div>
    </template>
  </div>
</template>
